import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RapItemDialog } from "@/components/RapItemDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { fmtOrEmpty } from "@/lib/format";
import { Loader2, PackageSearch } from "lucide-react";

const STATUSES = ["Belum Dipesan", "Dipesan", "Datang", "Terpasang"];
const CLS = { "Belum Dipesan": "bg-slate-100 text-slate-700", Dipesan: "bg-amber-100 text-amber-800", Datang: "bg-sky-100 text-sky-800", Terpasang: "bg-emerald-100 text-emerald-800" };
const PROJECT_ROLES = ["Owner", "Manager", "Finance", "Admin Project", "Kepala Produksi", "Foreman"];

export default function Spareparts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("all");
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    api.get("/spareparts", { params: status === "all" ? {} : { status } }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [status]);
  useEffect(() => { load(); }, [load]);
  const canOpen = user && PROJECT_ROLES.includes(user.role);
  const canPrice = user && ["Owner", "Manager", "Finance", "Purchasing"].includes(user.role);
  return (
    <div className="space-y-4" data-testid="spareparts-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><h1 className="font-heading text-2xl font-bold text-navy">Sparepart Proyek</h1>
          <p className="text-sm text-muted-foreground">Kebutuhan, kedatangan, dan pemasangan sparepart dari RAP setiap proyek.</p></div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[180px]" data-testid="sparepart-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Status</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {!rows ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-primary" /></div> : rows.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground"><PackageSearch className="mx-auto mb-2 opacity-40" />Belum ada sparepart pada RAP proyek.</Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-xs min-w-[720px]">
            <thead className="bg-slate-50 text-muted-foreground"><tr className="text-left">
              <th className="p-2.5">Proyek</th><th className="p-2.5">Sparepart</th><th className="p-2.5 text-right">Kebutuhan</th><th className="p-2.5 text-right">Diterima</th>
              <th className="p-2.5 text-right">Terpasang</th>{canPrice && <th className="p-2.5 text-right">Harga Rencana</th>}<th className="p-2.5">Status</th><th className="p-2.5"></th>
            </tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id} className={`border-t ${canOpen ? "cursor-pointer hover:bg-slate-50" : ""}`} onClick={() => canOpen && navigate(`/projects/${r.project_uuid}`)} data-testid={`sparepart-row-${r.id}`}>
                <td className="p-2.5"><div className="font-mono-k3 text-primary font-semibold">{r.project_id}</div><div className="text-[10px]">{r.plate}</div></td>
                <td className="p-2.5"><div className="font-semibold text-navy">{r.name}</div><div className="text-[10px] text-muted-foreground">{[r.code, r.supplier].filter(Boolean).join(" · ") || "-"}</div></td>
                <td className="p-2.5 text-right">{r.qty_plan} {r.unit}</td><td className="p-2.5 text-right">{r.qty_received}</td><td className="p-2.5 text-right">{r.qty_installed}</td>
                {canPrice && <td className="p-2.5 text-right">{r.plan_price == null ? (r.price_status === "Menunggu Penetapan Harga" ? "Belum Diinput" : "Tersembunyi") : `${fmtOrEmpty(r.plan_price)} (${r.price_status})`}</td>}
                <td className="p-2.5"><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${CLS[r.status]}`}>{r.status}</span></td>
                <td className="p-2.5">{r.editable?.length > 0 && <Button size="sm" variant="outline" className="h-7 px-2" onClick={(e) => { e.stopPropagation(); setEdit(r); }} data-testid={`sparepart-edit-${r.id}`}>Ubah</Button>}</td>
              </tr>
            ))}</tbody>
          </table>
        </Card>
      )}
      <RapItemDialog item={edit} kind="sparepart" statuses={STATUSES} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
